import * as ExcelJS from 'exceljs';
import {
  getTotalDepositAmount,
  getTotalWithdrawalAmount,
  getBalanceDifference,
} from '../../component/maturity-schedule/MaturityTableUtils';
import { BaseExcelService } from './baseExcelService';
import {
  MultiExportData,
  ExcelColors,
  EnrichedMaturityScheduleForExport,
} from './types';

/**
 * Service for exporting multiple maturity schedules to Excel
 */
export class MultiMaturityExportService {
  /**
   * Export multiple maturity schedules to Excel summary report
   */
  static async exportToExcel(data: MultiExportData): Promise<void> {
    const { records, month, year } = data;

    // Create workbook
    const workbook = new ExcelJS.Workbook();
    const worksheet = workbook.addWorksheet('Báo cáo tổng hợp');

    // Set column widths - Theo layout ảnh
    worksheet.columns = [
      { width: 5 }, // A - STT
      { width: 20 }, // B - Tên khách hàng
      { width: 20 }, // C - Tên chủ thẻ
      { width: 15 }, // D - Số thẻ
      { width: 20 }, // E - Ngân hàng
      { width: 15 }, // F - Tổng đáo
      { width: 15 }, // G - Rút
      { width: 10 }, // H - Phí (%)
      { width: 15 }, // I - Chênh lệch
      { width: 15 }, // J - Thành tiền
      { width: 15 }, // K - Phí CTV
      { width: 15 }, // L - HH
    ];

    // 1. Main title
    worksheet.mergeCells('A1:L1');
    BaseExcelService.createHeaderCell(
      worksheet,
      'A1',
      'HÓA ĐƠN TỔNG HỢP DỊCH VỤ ĐÁO HẠN THẺ TÍN DỤNG',
      16,
      ExcelColors.PRIMARY_HEADER
    );

    // 2. Report period and info
    MultiMaturityExportService.addReportInfo(
      worksheet,
      month,
      year,
      records.length
    );

    // 3. Table header - Theo ảnh
    const startRow = 8;
    const headers = [
      'STT', // A
      'Tên khách hàng', // B
      'Tên chủ thẻ', // C
      'Số thẻ', // D
      'Ngân hàng', // E
      'Tổng đáo', // F
      'Rút', // G
      'Phí (%)', // H
      'Chênh lệch', // I
      'Thành tiền', // J
      'Phí CTV', // K
      'HH', // L
    ];
    BaseExcelService.createTableHeader(worksheet, startRow, headers);

    // 4. Data rows and calculate totals
    const totals = MultiMaturityExportService.addDataRows(
      worksheet,
      records,
      startRow + 1
    );

    // 5. Add total row as part of the main table
    MultiMaturityExportService.addTotalRowToTable(
      worksheet,
      totals,
      startRow + records.length + 1
    );

    // 6. Summary section
    MultiMaturityExportService.addSummarySection(
      worksheet,
      records.length,
      totals,
      startRow + records.length + 3
    );

    // Generate and save file
    const fileName = BaseExcelService.generateFileName(
      'BaoCaoTongHop',
      `Thang${month}_${year}`
    );
    await BaseExcelService.saveExcelFile(workbook, fileName);
  }

  /**
   * Add report period and basic information
   */
  private static addReportInfo(
    worksheet: ExcelJS.Worksheet,
    month: number,
    year: number,
    recordCount: number
  ): void {
    // Report period
    worksheet.getCell('A3').value = 'Kỳ báo cáo:';
    worksheet.getCell('A3').font = { bold: true };
    worksheet.getCell('B3').value = `Tháng ${month}/${year}`;

    // Export date
    worksheet.getCell('A4').value = 'Ngày xuất:';
    worksheet.getCell('A4').font = { bold: true };
    worksheet.getCell('B4').value = BaseExcelService.formatVietnameseDate(
      new Date()
    );

    // Record count
    worksheet.getCell('A5').value = 'Số kỳ đáo hạn:';
    worksheet.getCell('A5').font = { bold: true };
    worksheet.getCell('B5').value = `${recordCount} kỳ`;
  }

  /**
   * Add data rows and calculate totals
   */
  private static addDataRows(
    worksheet: ExcelJS.Worksheet,
    records: EnrichedMaturityScheduleForExport[],
    startRow: number
  ): {
    totalRepayment: number;
    totalDeposit: number;
    totalWithdrawal: number;
    totalFee: number;
    totalBalance: number;
    totalCtvCommission: number;
    totalMargin: number;
    totalAmount: number;
  } {
    const totals = {
      totalRepayment: 0,
      totalDeposit: 0,
      totalWithdrawal: 0,
      totalFee: 0,
      totalBalance: 0,
      totalCtvCommission: 0,
      totalMargin: 0,
      totalAmount: 0,
    };

    records.forEach((record, index) => {
      const row = startRow + index;

      // Extract basic data
      const customerName = record.creditCard?.customer?.name || 'N/A';
      const cardHolderName = record.creditCard?.cardHolderName || 'N/A';
      const cardNumber = record.creditCard?.cardNumber?.slice(-4) || 'N/A';

      // Bank name only (without card type based on image)
      const bankName = record.creditCard?.bank?.name || 'N/A';

      const repaymentAmount = record.repaymentAmount || 0;

      // Use utility functions directly like in the modal preview
      const depositAmount = getTotalDepositAmount(record);
      const withdrawalAmount = getTotalWithdrawalAmount(record);
      const balanceDiff = getBalanceDifference(record);

      const feeRate = record.feeApplied || 0;

      // CTV commission rate — đọc từ trường top-level commissionRateForPartner
      // mà MultiExportModal đã set trong transform. Trước đây đọc từ
      // record.customerDetails?.commissionRateForPartner — trường KHÔNG tồn
      // tại nên luôn = 0 ⇒ cột "Phí CTV" toàn 0.00% (bug khách báo 2026-05-29).
      const ctvCommissionRate = (record as any).commissionRateForPartner || 0;

      // Hiển thị cột "Phí CTV" = (Phí thẻ - Phí CTV) — đây là rate hoa hồng
      // chênh lệch mà công ty giữ lại, dùng để * Rút ra HH (cột L).
      // Customer 2026-05-29: "phí của thẻ - phí ctv sẽ ra hoa hồng chênh lệch
      // * với phí rút".
      //   - Có CTV (vd cardFee=1.5%, CTV=0.5%): hiển thị "1.00%"
      //   - Không CTV (CTV=0, cardFee=defaultFeeRate): hiển thị defaultFeeRate
      const marginRate = feeRate - ctvCommissionRate;
      const ctvDisplay = `${marginRate.toFixed(2)}%`;

      // Calculate total amount = rút * phí% + chênh lệch (like in image)
      const totalAmount = (withdrawalAmount * feeRate) / 100 + balanceDiff;

      // Calculate HH (Hoa hồng) = rút * (phí% - phí ctv%).
      // ctvCommissionRate = 0 khi không có CTV ⇒ margin = full fee (đúng:
      // công ty giữ toàn bộ phí cho khách lẻ).
      const marginAmount =
        (withdrawalAmount * (feeRate - ctvCommissionRate)) / 100;

      // Update totals
      totals.totalRepayment += repaymentAmount;
      totals.totalDeposit += depositAmount;
      totals.totalWithdrawal += withdrawalAmount;
      totals.totalFee += (withdrawalAmount * feeRate) / 100;
      totals.totalBalance += balanceDiff;
      totals.totalCtvCommission += (ctvCommissionRate * withdrawalAmount) / 100;
      totals.totalMargin += marginAmount;
      totals.totalAmount += totalAmount;

      const rowData = [
        index + 1, // STT
        customerName,
        cardHolderName,
        `***${cardNumber}`,
        bankName,
        depositAmount, // Tổng đáo = Tổng nạp (thay vì repaymentAmount)
        withdrawalAmount,
        feeRate,
        balanceDiff,
        totalAmount,
        ctvDisplay,
        marginAmount,
      ];

      rowData.forEach((cellData, colIndex) => {
        const cell = worksheet.getCell(row, colIndex + 1);
        cell.value = cellData;

        // Format currency columns (Tổng đáo, Rút, Chênh lệch, Thành tiền, HH) - không VND
        if (
          [5, 6, 8, 9, 11].includes(colIndex) &&
          typeof cellData === 'number'
        ) {
          cell.numFmt = '#,##0'; // Format number with comma but no currency symbol
          BaseExcelService.applyBorder(cell);
        }
        // Format percentage column (Phí %)
        else if (colIndex === 7 && typeof cellData === 'number') {
          cell.numFmt = '0.00"%"'; // Format as number with % symbol, not percentage format
          BaseExcelService.applyBorder(cell);
        }
        // Apply border for other cells
        else {
          BaseExcelService.applyBorder(cell);
        }
      });
    });

    return totals;
  }

  /**
   * Add total row as part of the main table
   */
  private static addTotalRowToTable(
    worksheet: ExcelJS.Worksheet,
    totals: {
      totalRepayment: number;
      totalDeposit: number;
      totalWithdrawal: number;
      totalFee: number;
      totalBalance: number;
      totalCtvCommission: number;
      totalMargin: number;
      totalAmount: number;
    },
    startRow: number
  ): void {
    // Add total row as part of the main table
    const totalRowData = [
      'Tổng', // STT
      '', // Tên khách hàng
      '', // Tên chủ thẻ
      '', // Số thẻ
      '', // Ngân hàng
      totals.totalDeposit, // Tổng đáo
      totals.totalWithdrawal, // Rút
      '', // Phí (%)
      '', // Chênh lệch
      totals.totalAmount, // Thành tiền
      '', // Phí CTV
      totals.totalMargin, // HH
    ];

    totalRowData.forEach((cellData, colIndex) => {
      const cell = worksheet.getCell(startRow, colIndex + 1);
      cell.value = cellData;

      // Format currency columns (Tổng đáo, Rút, Thành tiền, HH)
      if ([5, 6, 9, 11].includes(colIndex) && typeof cellData === 'number') {
        cell.numFmt = '#,##0'; // Format number with comma but no currency symbol
        BaseExcelService.applyBorder(cell);
      }
      // Apply border for other cells
      else {
        BaseExcelService.applyBorder(cell);
      }
    });
  }

  /**
   * Add summary section with totals
   */
  private static addSummarySection(
    worksheet: ExcelJS.Worksheet,
    recordCount: number,
    totals: {
      totalRepayment: number;
      totalDeposit: number;
      totalWithdrawal: number;
      totalFee: number;
      totalBalance: number;
      totalCtvCommission: number;
      totalMargin: number;
      totalAmount: number;
    },
    startRow: number
  ): void {
    // Summary header
    worksheet.mergeCells(`A${startRow}:L${startRow}`);
    BaseExcelService.createHeaderCell(
      worksheet,
      `A${startRow}`,
      'TỔNG KẾT',
      12,
      ExcelColors.SECONDARY_HEADER
    );

    const summaryData = [
      ['Tổng số kỳ đáo hạn:', recordCount],
      ['Tổng tiền đáo hạn:', totals.totalRepayment],
      ['Tổng đã nạp:', totals.totalDeposit],
      ['Tổng đã rút:', totals.totalWithdrawal],
      ['Chênh lệch:', totals.totalBalance],
      ['Tổng thành tiền:', totals.totalAmount],
      ['HH CTV:', totals.totalMargin], // Sử dụng totalMargin (tổng của cột HH) thay vì totalCtvCommission
    ];

    summaryData.forEach((data, index) => {
      const row = startRow + 1 + index;

      // Label column
      const labelCell = worksheet.getCell(`A${row}`);
      labelCell.value = data[0];
      labelCell.font = { bold: true };
      BaseExcelService.applyBorder(labelCell);

      // Value column - chỉ hiển thị số, không có đơn vị VND
      const valueCell = worksheet.getCell(`C${row}`); // Đặt ở cột C như trong ảnh
      valueCell.value = data[1];

      if (typeof data[1] === 'number') {
        // Format số với dấu phẩy nhưng không có VND
        valueCell.numFmt = '#,##0';
      }

      BaseExcelService.applyBorder(valueCell);

      // Special formatting for final total (Tổng thành tiền)
      if (index === 5) {
        // Tổng thành tiền
        BaseExcelService.applyCellStyle(labelCell, {
          font: { bold: true },
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' }, // Light gray background
          },
        });

        BaseExcelService.applyCellStyle(valueCell, {
          font: { bold: true },
          fill: {
            type: 'pattern',
            pattern: 'solid',
            fgColor: { argb: 'FFE0E0E0' }, // Light gray background
          },
        });
      }
    });

    // Add empty cell with border in column D after summary (based on image)
    const emptyCell = worksheet.getCell(`D${startRow + 7}`);
    BaseExcelService.applyBorder(emptyCell);
  }
}
